"use client";

import HeroV3Background from "./HeroV3Background";
import HeroV3CTA from "./HeroV3CTA";
import HeroV3Showcase from "./HeroV3Showcase";
import HeroV3TopTicker from "./HeroV3TopTicker";
import HeroV3BottomStrip from "./HeroV3BottomStrip";
import HeroV3Heading from "./HeroV3Heading";
import HeroV3StatsRow from "./HeroV3StatsRow";
import CdnImage from "@/components/CdnImage";
import { STATIC_ASSETS } from "@/lib/cdn";
// Smokey cursor — deferred client component (desktop only, after LCP).
import HeroDesktopEffects from "@/app/sections/HeroDesktopEffects";
import type { TapeEvent } from "./types";
import { useT } from "@/hooks/useT";

/**
 * HeroV3 — the new FrenFlow hero.
 *
 * Composition (top → bottom):
 *   1. HeroV3TopTicker   — infinite two-track marquee of trending markets
 *                          with live odds + 24h delta. Reads alive on first
 *                          paint; CSS-only animation, zero JS per frame.
 *   2. Main area          — asymmetric 45/55 split.
 *        Left (45%):  headline + subhead + CTAs + aggregate stats strip
 *        Right (55%): HeroV3Showcase — copy-trading cockpit: 3 floating
 *                     cards driven by the real trader leaderboard
 *                     (Same-block copy proof, Most-copied + Copy CTA,
 *                     Top traders to copy). Always feels populated.
 *   3. HeroV3BottomStrip — thin horizontal live-trade strip that bridges
 *                          the hero to the next section.
 *
 * Signature elements kept from the legacy hero:
 *  - Smokey cursor trail (desktop, deferred 2s).
 *  - Subtle gradient mesh in the background.
 *
 * Stats framing: instead of showing our own (small) collected volume,
 * we surface the *addressable* liquidity across the markets we route to
 * — we're the interface, not the exchange. The absolute numbers live in
 * `/fees/frenflow` on DeFiLlama once the adapter lands.
 */

interface PlatformStats {
  total_volume: number;
  /** All-time Polymarket builder volume (official builders leaderboard). */
  builder_volume?: number | null;
  total_trades: number;
  total_markets: number;
  active_users_24h?: number;
}

interface MarketSnapshot {
  slug: string;
  title: string;
  image?: string | null;
  yesPrice: number;
  priceDelta24h?: number;
  volume24hr: number;
  /** Sparkline: historical yes-prices, 0-1 normalized (most recent last). */
  yesHistory?: number[];
  /** Predexon smart-money signal, optional. */
  smartMoneySignal?: {
    topSide: "YES" | "NO";
    pct: number;
    walletCount: number;
  } | null;
  conditionId?: string;
  /** Slug of the leading sub-market — used for `?market=<slug>` deep links. */
  marketSlug?: string;
  /** True when the parent event has > 1 sub-market. */
  isMultiOutcome?: boolean;
  /** Human label for the leading outcome ("Lakers", "30 days"). Falls
   *  back to "Yes" on single-outcome events. */
  outcomeLabel?: string;
}

interface TraderSnapshot {
  wallet?: string;
  user_id?: string;
  username?: string;
  display_name?: string;
  image?: string | null;
  pnl_30d?: number;
  pnl?: number;
  copiers_count?: number;
}

export interface WhaleSnapshot {
  id: string;
  side: "BUY" | "SELL";
  wallet: string;
  walletName: string;
  marketSlug: string;
  /** Parent event slug — drives the working /polymarket/event/<eventSlug> link. */
  eventSlug?: string;
  /** True when the parent event has > 1 outcome (controls ?market=<slug>). */
  isMultiOutcome?: boolean;
  marketTitle: string;
  outcome: string;
  sizeUsd: number;
  tier: string;
  accuracy?: number;
  detectedAt?: string;
}

interface HeroV3Props {
  platformStats?: PlatformStats | null;
  initialTape?: TapeEvent[];
  /** Trending markets for the ticker + top-mover card. */
  markets?: MarketSnapshot[];
  /** Top traders for the smart-money card. */
  traders?: TraderSnapshot[];
  /** Real whale trades from /api/social/activity/whales. */
  whales?: WhaleSnapshot[];
  /** Market-move events (price swings) from the activity stream. */
  moves?: MoveSnapshot[];
  /** Aggregated addressable-liquidity figure in USD (Polymarket + Kalshi). */
  addressableLiquidityUsd?: number | null;
  /** Bulk PnL sparkline series keyed by trader address. */
  traderSparklines?: Record<string, number[]>;
}

export interface MoveSnapshot {
  id: string;
  marketSlug: string;
  /** Parent event slug from markets_cache (set by /api/activity/stream
   *  enrichment). Used to build a working `/polymarket/event/...` link. */
  eventSlug?: string;
  /** True when the parent event has > 1 sub-market — drives whether we
   *  append `?market=<slug>` so the page lands on the right outcome. */
  isMultiOutcome?: boolean;
  marketTitle: string;
  marketImage?: string | null;
  outcome: string;
  yesPrice: number;
  priceDelta24h: number;
  detectedAt?: string;
}

export default function HeroV3({
  platformStats,
  initialTape = [],
  markets = [],
  traders = [],
  whales = [],
  moves = [],
  addressableLiquidityUsd,
  traderSparklines,
}: HeroV3Props) {
  const t = useT();
  return (
    <section
      data-hero-bounds
      // min-h leaves room above the LiveTicker (h-11 = 2.75rem on lg+).
      // No max-h: if the content is taller than the viewport (small
      // monitors) the section grows so nothing gets clipped — the badge
      // and stats row stay visible and aligned via mt-auto in each
      // flex-col column.
      className="relative overflow-hidden bg-white dark:bg-[#0a0a0b] flex flex-col min-h-[calc(100dvh-7rem)] lg:min-h-[calc(100dvh-8rem-2.75rem)]"
    >
      {/* Gradient mesh + faint grid — cursor-follow on desktop */}
      <HeroV3Background />

      {/* Smokey cursor trail — deferred, desktop only */}
      <HeroDesktopEffects />

      {/* Top marquee — loads first, makes the page feel alive instantly.
          Edge-to-edge: no outer container, no lateral padding. */}
      <div className="relative z-20 shrink-0">
        <HeroV3TopTicker markets={markets.slice(0, 10)} />
      </div>

      {/* Main area — grows to fill the available viewport space so the
          headline sits vertically centered between the top marquee and
          the bottom live strip. Edge-to-edge on desktop. */}
      <div className="relative z-10 flex-1 flex items-center px-4 sm:px-6 lg:px-8 xl:px-12 py-10 lg:py-[clamp(28px,5vh,56px)]">
        <div className="w-full grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-10 lg:gap-14 items-stretch">
          {/* ── LEFT: text + CTAs + aggregate stats ───────────── */}
          <div className="flex flex-col">
            <div className="self-start inline-flex items-center gap-2 mb-6 lg:mb-[clamp(16px,3vh,32px)] px-3 py-1.5 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-100 dark:bg-[#18181D] backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <span className="font-inter-tight text-[11px] font-medium text-gray-600 dark:text-gray-400 tracking-wide uppercase">
                {t("home.hero.statusPill")}
              </span>
            </div>

            {/* H1 — staged reveal (opacity + y + blur), see HeroV3Heading */}
            <HeroV3Heading />

            <p className="mt-6 lg:mt-[clamp(16px,3vh,32px)] font-inter-tight text-base lg:text-lg text-gray-600 dark:text-gray-400 max-w-xl leading-relaxed">
              {t("home.hero.subhead")}
            </p>

            <div className="mt-8 lg:mt-[clamp(16px,3vh,40px)]">
              <HeroV3CTA />
            </div>

            {/* Connected venues — reinforces "aggregator / routes to real
                liquidity" messaging visually, not just in copy. */}
            <VenueRow />

            {/* Aggregate stats row — count-up animation on scroll-in.
                mt-auto pushes it to the bottom of the left column so it
                shares a baseline with the BackedByBadge in the right. */}
            <div className="mt-auto pt-4">
              <HeroV3StatsRow
                addressableLiquidityUsd={addressableLiquidityUsd}
                totalVolume={(platformStats?.builder_volume ?? platformStats?.total_volume) ?? 0}
              />
            </div>
          </div>

          {/* ── RIGHT: live showcase cockpit ─────────────────── */}
          <div className="relative flex flex-col">
            <HeroV3Showcase
              markets={markets.slice(0, 6)}
              traders={traders.slice(0, 6)}
              tape={initialTape}
              whales={whales.slice(0, 5)}
              moves={moves.slice(0, 6)}
              traderSparklines={traderSparklines}
            />
            {/* Backed by — pushed to the bottom of the right column so
                it lines up horizontally with the StatsRow on the left. */}
            <div className="mt-auto pt-4">
              <BackedByBadge />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom scrolling strip — pinned to the hero's bottom edge so it
          butts right up against the global LiveTicker footer. Zero gap
          between the two, visually one continuous ribbon of live data. */}
      <div className="relative z-20 shrink-0 mt-auto">
        <HeroV3BottomStrip events={initialTape} />
      </div>
    </section>
  );
}

function VenueRow() {
  const t = useT();
  // Same icon style the header SourceSelector uses: a rounded colored
  // square with a small white mark inside. Keeps the hero consistent
  // with the chrome the user already sees elsewhere in the app.
  const venues = [
    {
      name: "Polymarket",
      chain: "Polygon",
      logo: "/logos/polymarket/icon-white.png",
      color: "#2E5CFF",
      href: "/polymarket/markets/trending",
    },
    {
      name: "Kalshi",
      chain: "Solana",
      logo: "/logos/kalshi/k-icon.svg",
      color: "#09C285",
      href: "/kalshi/markets/trending",
    },
    {
      name: "Hyperliquid",
      chain: "HyperCore",
      logo: "/logos/hyperliquid/symbol-mint.svg",
      color: "#97FCE4",
      logoBg: "#0f1a1f",
      href: "/hyperliquid/markets/trending",
    },
  ];

  return (
    <div className="mt-8 lg:mt-[clamp(16px,3vh,40px)]">
      <div className="font-inter-tight text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
        {t("home.hero.tradeAcross")}
      </div>
      <div className="flex flex-wrap items-center gap-2.5">
        {venues.map((v) => (
          <a
            key={v.name}
            href={v.href}
            className="group inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-100 dark:bg-white/[0.02] hover:bg-white dark:hover:bg-white/[0.05] hover:border-gray-300 dark:hover:border-white/20 transition-colors"
          >
            <span
              className="flex-shrink-0 w-6 h-6 rounded-[5px] flex items-center justify-center"
              style={{ backgroundColor: (v as any).logoBg ?? v.color }}
            >
              {v.logo ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={v.logo}
                  alt={`${v.name} logo`}
                  className="w-4 h-4 object-contain"
                />
              ) : (
                <span className="font-inter-tight text-[11px] font-black text-black leading-none">
                  P
                </span>
              )}
            </span>
            <span className="font-inter-tight text-[13px] font-semibold text-gray-900 dark:text-white">
              {v.name}
            </span>
            <span className="font-inter-tight text-[10px] text-gray-400 dark:text-gray-500 hidden sm:inline">
              · {v.chain}
            </span>
          </a>
        ))}
      </div>
    </div>
  );
}

function BackedByBadge() {
  const t = useT();
  return (
    // No longer absolute — rendered as the last child of the right column
    // so flex layout naturally aligns it with the StatsRow at the bottom
    // of the left column.
    <div className="flex items-center gap-2.5 justify-end pointer-events-auto">
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-white/80 dark:bg-[#0a0a0b]/70 backdrop-blur-sm">
        <span className="font-inter-tight text-[10px] font-semibold uppercase tracking-widest text-white">
          {t("home.hero.backedBy")}
        </span>
        <a
          href="https://www.dextools.io"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center hover:opacity-80 transition-opacity"
          aria-label="DexTools"
        >
          {/* Local public asset — same path the markets page + news
              notifications use (NewsNotificationsContext), guaranteed to
              resolve on every deploy. eslint-disable-next-line for the
              raw <img>: next/image here would only hurt for a tiny logo. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/logos/dextools-logo.png"
            alt="DexTools"
            className="h-[24px] w-auto"
            loading="lazy"
          />
        </a>
      </div>
    </div>
  );
}

